import {useCallback} from "react";
import {useQueryClient} from "@tanstack/react-query";
import type {Method} from "axios";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {profileHeaderUrl, recordUrl} from "../services/categoryProfileUrls";
import type {CategoryRecord, RecordWriteResponse} from "../types/categoryRecordTypes";

/** The authored record of one category. An empty record (`exists: false`) until somebody writes. */
export function useCategoryRecordQuery(databagId: number, path: string) {
    const url = recordUrl(databagId, path);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<RecordWriteResponse>(url)).data.record,
        enabled: databagId > 0 && Boolean(path),
        retry: false,
    });
}

/** Sends one write and puts the record from the answer in the cache, so every section updates at once. */
export function useRecordWrite(databagId: number, path: string) {
    const queryClient = useQueryClient();
    return useCallback(async (method: Method, url: string, data?: unknown): Promise<RecordWriteResponse> => {
        // queryHttp defaults to JSON, which makes axios turn FormData into JSON and drop the file. Multipart instead;
        // the browser then sets the boundary.
        const headers = data instanceof FormData ? {'Content-Type': 'multipart/form-data'} : undefined;
        const response = await queryHttp.request<RecordWriteResponse>({method, url, data, headers});
        queryClient.setQueryData<CategoryRecord>([recordUrl(databagId, path)], response.data.record);
        // Dismissing or restoring a detected risk changes the header badges (any savings year)
        if (url.includes('risk')) {
            const [base, query] = profileHeaderUrl(databagId, path).split('?');
            void queryClient.invalidateQueries({predicate: q => {
                const key = String(q.queryKey[0]);
                return key.startsWith(`${base}?`) && key.includes(query);
            }});
        }
        return response.data;
    }, [queryClient, databagId, path]);
}
